import React from 'react';
import { Box, Typography, Tooltip, CircularProgress, Skeleton, Fade, useMediaQuery, useTheme } from '@mui/material';
import { useSubscriptionUsage } from '../context/SubscriptionUsageContext';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import InsertPhotoRoundedIcon from '@mui/icons-material/InsertPhotoRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import { formatUTCDate } from '../hooks/useActiveSubscription'; // Reusando el helper de fechas del dashboard

export const UsageIndicators = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { usageInfo } = useSubscriptionUsage();

  if (usageInfo.isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    );
  }

  // Si retornó 404/no suscripción, ocultamos los contadores
  if (!usageInfo.hasSubscription) {
    return null; 
  }

  const getProgressVal = (used, limit) => {
    if (limit === 0) return 0;
    const progress = (used / limit) * 100;
    return progress > 100 ? 100 : progress;
  };

  const getProgressColor = (used, limit) => {
    const progress = (used / limit) * 100;
    if (progress >= 90) return '#f73541'; // Rojo crítico
    if (progress >= 75) return '#f59e0b'; // Naranja warning
    return '#10b981'; // Verde ok
  };

  const MetricCircle = ({ used, limit, icon: Icon, title, resetDate }) => {
    const color = getProgressColor(used, limit);
    const value = getProgressVal(used, limit);

    return (
      <Tooltip 
        title={
          <Box sx={{ p: 0.5, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
            <Typography variant="body2">{used} / {limit}</Typography>
            {resetDate && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#fbbf24' }}>
                Se reinicia el: {formatUTCDate(resetDate)}
              </Typography>
            )}
          </Box>
        }
        arrow
        TransitionComponent={Fade}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <Box sx={{ 
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 1.5,
            animation: 'pulse 2.5s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
              '100%': { transform: 'scale(1)' }
            }
          }}>
            <CircularProgress 
              variant="determinate" 
              value={100} 
              size={36} 
              thickness={4} 
              sx={{ color: '#e2e8f0' }} 
            />
            <CircularProgress 
              variant="determinate" 
              value={value} 
              size={36} 
              thickness={4} 
              sx={{ 
                color: color,
                position: 'absolute',
                left: 0,
                strokeLinecap: 'round'
              }} 
            />
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon sx={{ fontSize: 18, color: '#0f172a' }} />
            </Box>
          </Box>
          {!isMobile && (
            <Box sx={{ ml: 0.8, display: 'flex', flexDirection: 'column', pr: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2, fontFamily: 'Como, sans-serif' }}>
                {used} / {limit}
              </Typography>
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: isMobile ? 0.5 : 1, 
      alignItems: 'center', 
      mr: isMobile ? 0 : 2 
    }}>
      <MetricCircle 
        used={usageInfo.products.used} 
        limit={usageInfo.products.limit} 
        icon={RestaurantMenuRoundedIcon}
        title={isMobile ? "Prod." : "Productos (Histórico)"}
      />
      <MetricCircle 
        used={usageInfo.images.used} 
        limit={usageInfo.images.limit} 
        icon={InsertPhotoRoundedIcon}
        title={isMobile ? "Img." : "Imágenes (Histórico)"}
      />
      <MetricCircle 
        used={usageInfo.ai_prompts.used} 
        limit={usageInfo.ai_prompts.limit} 
        icon={AutoFixHighRoundedIcon}
        title="Mejoras IA"
        resetDate={usageInfo.billing_period_end}
      />
    </Box>
  );
};
